import { Box, getVariant, Input, Select } from '@apia/theme';
import { observer } from 'mobx-react-lite';
import { TFilterRenderer } from './types';
import { useRef } from 'react';
import { TApiaFilterValue, useIntermediateValue } from '@apia/util';

const units = ['B', 'KB', 'MB', 'GB'];

export const RangeFilterRenderer: TFilterRenderer = observer(({ state }) => {
  const multiplier = useRef(1);
  const lastEmittedHigh = useRef<TApiaFilterValue | null>(null);
  const lastEmittedLow = useRef<TApiaFilterValue | null>(null);
  const lastEmittedMultiplier = useRef(1);

  if (lastEmittedLow.current !== state.value) {
    lastEmittedLow.current = state.value;
    lastEmittedHigh.current = state.filterToValue || '';
  }

  const emitLow = (currentValue: TApiaFilterValue) => {
    const numberValue = currentValue ?? '';

    if (
      lastEmittedLow.current === numberValue &&
      lastEmittedMultiplier.current === multiplier.current
    ) {
      return;
    }

    lastEmittedLow.current = numberValue;
    lastEmittedMultiplier.current = multiplier.current;
    const emitValue = numberValue;

    state.value = emitValue;
    state.multiplier = multiplier.current;
  };

  const emitHigh = (currentValue: TApiaFilterValue) => {
    const numberValue = currentValue ?? '';

    if (
      lastEmittedHigh.current === numberValue &&
      lastEmittedMultiplier.current === multiplier.current
    ) {
      return;
    }

    lastEmittedHigh.current = numberValue;
    lastEmittedMultiplier.current = multiplier.current;
    const emitValue = numberValue;

    state.filterToValue = String(emitValue);
    state.multiplier = multiplier.current;
  };

  const [lowValue, setLowValue] = useIntermediateValue(state.value);
  const [highValue, setHighValue] = useIntermediateValue(state.filterToValue);
  const [unit, setUnit] = useIntermediateValue(state.multiplier);

  return (
    <Box
      className={`ApiaFilter__Range`}
      {...getVariant('layout.common.components.filters.rangeFilter')}
    >
      <Box className="ApiaFilter__Range__Low">
        <Input
          data-query-filter-id={state.id}
          data-query-filter-part="value"
          disabled={state.readonly}
          onChange={({ target: { value: currentValue } }) => {
            setLowValue(currentValue);
            emitLow(currentValue);
          }}
          onBlur={({ target: { value: currentValue } }) => {
            emitLow(currentValue);
          }}
          onKeyDown={({ code, target }) => {
            if (code === 'Enter') {
              emitLow((target as HTMLInputElement).value);
            }
          }}
          value={lowValue}
        />
      </Box>
      {' - '}
      <Box className="ApiaFilter__Range__High">
        <Input
          data-query-filter-id={state.id}
          data-query-filter-part="filterToValue"
          disabled={state.readonly}
          required={state.required}
          onChange={({ target: { value: currentValue } }) => {
            setHighValue(currentValue);
            emitHigh(currentValue);
          }}
          onBlur={({ target: { value: currentValue } }) => {
            emitHigh(currentValue);
          }}
          onKeyDown={({ code, target }) => {
            if (code === 'Enter') {
              emitHigh((target as HTMLInputElement).value);
            }
          }}
          value={highValue}
        />
      </Box>
      {state.isSize && (
        <Select
          data-query-filter-id={state.id}
          data-query-filter-part="multiplier"
          disabled={state.readonly}
          className="ApiaFilter__Range__UnitSelector"
          onChange={(ev) => {
            const unit = ev.target.value;
            const index = units.indexOf(unit);
            if (index !== -1) {
              multiplier.current = 2 ** (index * 10);
              setUnit(multiplier.current);
              /**
               * It's enough to fire event on low because it updates the multiplier in the original filter
               */
              emitLow(lastEmittedLow.current ?? '');
            }
          }}
          value={units[Math.log2(unit || 1) / 10]}
        >
          {units.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      )}
    </Box>
  );
});
